import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import 'tom-select/dist/css/tom-select.css'
import '../stylesheets/tom-select.scss'

export default class extends Controller {
  static targets = ["select"]
  static values = {
    url: String,
    profileId: String
  }

  connect() {
    // Nur TomSelect initialisieren wenn ein Select-Target vorhanden ist
    if (this.hasSelectTarget) {
      this.initializeTomSelect()
    }
  }

  initializeTomSelect() {
    if (!this.hasSelectTarget) return;

    this.tomselect = new TomSelect(this.selectTarget, {
      valueField: 'id',
      labelField: 'text',
      searchField: ['text'],
      placeholder: "Kompetenz suchen oder neue erstellen...",
      openOnFocus: true,
      preload: 'focus',
      closeAfterSelect: true,
      selectOnTab: true,
      maxOptions: 100,
      create: true,
      createOnBlur: false,
      createFilter: (input) => {
        return input.length >= 2; // Mindestens 2 Zeichen für neue Kompetenz
      },
      create: (input) => {
        return {
          id: 'new_' + input,
          text: `"${input}" erstellen`,
          isNew: true
        };
      },
      load: (query, callback) => {
        if (!query.length) return callback();
        
        // Verwende die Backend-Such-API
        const url = `/de/backend/search/?model=competencies&term=${encodeURIComponent(query)}`
        
        fetch(url)
          .then(response => response.json())
          .then(json => {
            // Prüfe auf ähnliche Kompetenzen für Warnung
            const similarCompetencies = json.filter(item => 
              item.text.toLowerCase().includes(query.toLowerCase()) ||
              query.toLowerCase().includes(item.text.toLowerCase())
            );
            
            if (similarCompetencies.length > 0 && query.length >= 3) {
              // Füge Warnung hinzu
              json.unshift({
                id: 'warning',
                text: `⚠️ Ähnliche Kompetenzen gefunden: ${similarCompetencies.map(c => c.text).join(', ')}`,
                disabled: true
              });
            }
            
            callback(json)
          }).catch(() => {
            callback()
          })
      },
      render: {
        no_results: function(data, escape) {
          return '<div class="no-results">Keine Ergebnisse für "' + escape(data.input) + '"</div>';
        },
        option_create: function(data, escape) {
          return '<div class="create">Hinzuf&uuml;gen: <strong>' + escape(data.input) + '</strong>&hellip;</div>';
        },
        option: function(item, escape) {
          if (item.isNew) {
            return `<div class="option text-green-600">
              <span class="title text-sm font-medium">${escape(item.text)}</span>
              <span class="subtext text-xs text-green-500">Neue Kompetenz erstellen</span>
            </div>`
          } else if (item.id === 'warning') {
            return `<div class="option text-yellow-600 bg-yellow-50">
              <span class="title text-sm">${escape(item.text)}</span>
            </div>`
          } else {
            return `<div class="option">
              <span class="title text-sm">${escape(item.text)}</span>
              ${item.subtext ? `<span class="subtext text-xs text-gray-500">${item.subtext}</span>` : ''}
            </div>`
          }
        },
        item: function(item, escape) {
          if (item.isNew) {
            return `<div class="item text-sm text-green-600">${escape(item.text.replace(' erstellen', ''))}</div>`
          } else {
            return `<div class="item text-sm">${escape(item.text)}</div>`
          }
        }
      },
      onChange: (value) => {
        if (value) {
          this.addCompetency(value);
        }
      }
    })
  }

  addCompetency(competencyId) {
    const profileId = this.selectTarget.dataset.profileId;
    
    if (!profileId || profileId === 'new') {
      this.showError('Bitte speichern Sie das Profil zuerst, bevor Sie Kompetenzen hinzufügen.');
      this.tomselect.clear();
      return;
    }
    
    // Prüfe ob es eine neue Kompetenz ist
    if (competencyId.startsWith('new_')) {
      const newCompetencyTitle = competencyId.replace('new_', '');
      this.createCompetency(newCompetencyTitle, profileId);
    } else {
      // Verwende bestehende Kompetenz
      this.addCompetencyToProfile(competencyId, profileId);
    }
  }

  createCompetency(title, profileId) {
    const url = '/de/backend/competencies';
    const formData = new FormData();
    formData.append('competency[title]', title);
    // Für Skills: immer Tenant-Katalog verwenden
    formData.append('competency_type', 'skill');
    
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Jetzt füge die Kompetenz zum Profil hinzu
        this.addCompetencyToProfile(data.competency_id, profileId);
      } else {
        this.showError('Fehler beim Erstellen der Kompetenz: ' + (data.errors || 'Unbekannter Fehler'));
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Erstellen der Kompetenz');
    });
  }

  addCompetencyToProfile(applicantCompetencyId, profileId, competencyId) {
    const url = `/de/backend/profiles/${profileId}/profile_competencies`;
    const formData = new FormData();
    formData.append('profile_competency[applicant_competency_id]', applicantCompetencyId);
    formData.append('profile_competency[highlight]', 'false'); // Standard-Hervorhebung

    this.sendRequest(url, formData, competencyId);
  }

  toggleCompetency(event) {
    const checkbox = event.currentTarget;
    const competencyId = checkbox.dataset.competencyId;
    const applicantCompetencyId = checkbox.dataset.applicantCompetencyId;
    const profileId = this.profileIdValue || this.selectTarget?.dataset.profileId;
    
    if (!profileId || profileId === 'new') {
      this.showError('Bitte speichern Sie das Profil zuerst, bevor Sie Kompetenzen hinzufügen.');
      checkbox.checked = false;
      return;
    }
    
    if (checkbox.checked) {
      this.addCompetencyToProfile(applicantCompetencyId, profileId, competencyId);
    } else {
      this.removeCompetencyFromProfile(competencyId, profileId);
    }
  }

  removeCompetency(event) {
    const competencyId = event.currentTarget.dataset.competencyId;
    const profileId = this.profileIdValue || this.selectTarget?.dataset.profileId;
    
    if (profileId && profileId !== 'new') {
      this.removeCompetencyFromProfile(competencyId, profileId);
    }
  }

  removeCompetencyFromProfile(competencyId, profileId) {
    // Finde die ProfileCompetency ID für diese Kompetenz
    const competencyElement = document.querySelector(`[data-competency-id="${competencyId}"]`);
    const profileCompetencyId = competencyElement?.dataset.profileCompetencyId;
    
    if (!profileCompetencyId) {
      this.showError('Fehler beim Entfernen der Kompetenz: ID nicht gefunden');
      return;
    }
    
    const url = `/de/backend/profiles/${profileId}/profile_competencies/${profileCompetencyId}`;
    
    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (response.ok) {
        // Aktualisiere die UI - entferne Gewichtungsfeld und mache grau
        this.updateCompetencyUI(competencyId, false);
      } else {
        this.showError('Fehler beim Entfernen der Kompetenz');
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Entfernen der Kompetenz');
    });
  }

  sendRequest(url, formData, competencyId = null) {
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => {
      if (response.ok) {
        // Nur TomSelect leeren wenn es existiert
        if (this.tomselect) {
          this.tomselect.clear();
          this.tomselect.clearOptions();
        }
        
        // Aktualisiere die Checkbox und zeige Gewichtungsfeld
        if (competencyId) {
          this.updateCompetencyUI(competencyId, true);
        }
        
        // Verarbeite Turbo Stream Response
        return response.text();
      } else {
        return response.text().then(text => {
          this.showError('Fehler beim Hinzufügen der Kompetenz: ' + response.status);
          throw new Error('Request failed');
        });
      }
    })
    .then(html => {
      if (html) {
        // Verarbeite die Turbo Stream Response
        Turbo.renderStreamMessage(html);
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Hinzufügen der Kompetenz: ' + error.message);
    });
  }

  updateCompetencyUI(competencyId, isSelected) {
    const competencyElement = document.querySelector(`[data-competency-id="${competencyId}"]`);
    if (competencyElement) {
      if (isSelected) {
        competencyElement.classList.remove('opacity-50');
        // Füge Highlight-Toggle hinzu falls nicht vorhanden
        if (!competencyElement.querySelector('.flex.items-center.space-x-3')) {
          const controlsContainer = document.createElement('div');
          controlsContainer.className = 'flex items-center space-x-3';
          
          // Highlight Toggle
          const highlightDiv = document.createElement('div');
          highlightDiv.className = 'flex items-center space-x-1';
          highlightDiv.innerHTML = `
            <label class="text-xs text-gray-500">Hervorheben:</label>
            <input type="checkbox" name="profile[profile_competencies_attributes][${competencyId}][highlight]" 
                   value="1" class="rounded border-gray-300 text-blue-600 focus:ring-blue-600">
          `;
          
          controlsContainer.appendChild(highlightDiv);
          competencyElement.querySelector('.flex.items-center.justify-between').appendChild(controlsContainer);
        }
      } else {
        competencyElement.classList.add('opacity-50');
        // Entferne alle Kontrollfelder
        const controlsContainer = competencyElement.querySelector('.flex.items-center.space-x-3');
        if (controlsContainer) {
          controlsContainer.remove();
        }
      }
    }
  }

  getSkillCategoryId() {
    // Versuche die Skill-Kategorie-ID zu finden oder verwende einen Standard
    return 1; // Standard Skill-Kategorie ID
  }

  showError(message) {
    // Erstelle eine temporäre Fehlermeldung
    const errorDiv = document.createElement('div');
    errorDiv.className = 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4';
    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm">${message}</p>
        </div>
      </div>
    `;
    
    // Füge die Fehlermeldung vor dem Controller-Element hinzu
    this.element.parentNode.insertBefore(errorDiv, this.element);
    
    // Entferne die Fehlermeldung nach 5 Sekunden
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv);
      }
    }, 5000);
  }

  disconnect() {
    if (this.tomselect) {
      this.tomselect.destroy();
    }
  }
}
